/**
 * 模板选择对话框组件
 * 用于选择文档模板
 * 
 * @component TemplateSelectDialog
 * @props {Boolean} modelValue - 控制对话框显示/隐藏
 * @emits update:modelValue - 更新对话框显示状态
 * @emits template-selected - 模板选择完成事件
 */

<template>
  <el-dialog
    v-model="dialogVisible"
    title="选择模板"
    width="500px"
    :close-on-click-modal="false"
  >
    <div class="template-select-content">
      <el-select
        v-model="selectedTemplate"
        placeholder="请选择模板"
        class="template-select"
      >
        <el-option
          v-for="item in templateOptions"
          :key="item.value"
          :label="item.label"
          :value="item.value"
        >
          <div class="template-option">
            <el-icon><Files /></el-icon>
            <span>{{ item.label }}</span>
          </div>
        </el-option>
      </el-select>

      <div class="template-preview" v-if="selectedTemplate">
        <div class="preview-title">模板预览</div>
        <div class="preview-content">
          {{ getTemplateDescription(selectedTemplate) }}
        </div>
      </div>
    </div>
    
    <template #footer>
      <el-button @click="closeDialog">
        取消
      </el-button>
      <el-button 
        type="primary"
        @click="handleConfirm"
        :disabled="!selectedTemplate"
      >
        确定
      </el-button>
    </template>
  </el-dialog>
</template>

<script setup>
import { ref, watch } from 'vue'
import { Files } from '@element-plus/icons-vue'

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true
  }
})

const emit = defineEmits(['update:modelValue', 'template-selected'])

const dialogVisible = ref(props.modelValue)
const selectedTemplate = ref('')

const templateOptions = [
  { 
    value: 'standard', 
    label: '标准模板',
    description: '适用于大多数专利申请的标准模板，包含完整的专利文档结构。'
  },
  { 
    value: 'software', 
    label: '软件专利模板',
    description: '特别适用于软件相关发明的专利申请，突出算法和技术实现细节。'
  },
  { 
    value: 'hardware', 
    label: '硬件专利模板',
    description: '适用于硬件设备相关的专利申请，强调物理结构和工作原理。'
  },
  { 
    value: 'method', 
    label: '方法专利模板',
    description: '适用于方法或工艺流程相关的专利申请，突出步骤和过程说明。'
  }
]

const getTemplateDescription = (value) => {
  return templateOptions.find(option => option.value === value)?.description || ''
}

const handleConfirm = () => {
  if (selectedTemplate.value) {
    emit('template-selected', selectedTemplate.value)
    closeDialog()
  }
}

const closeDialog = () => {
  dialogVisible.value = false
  emit('update:modelValue', false)
  // 清理状态
  selectedTemplate.value = ''
}

// 监听 modelValue 变化
watch(() => props.modelValue, (newVal) => {
  dialogVisible.value = newVal
})

// 监听 dialogVisible 变化
watch(() => dialogVisible.value, (newVal) => {
  if (!newVal) {
    emit('update:modelValue', false)
  }
})
</script>

<style scoped lang="scss">
.template-select-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.template-select {
  width: 100%;
}

.template-option {
  display: flex;
  align-items: center;
  gap: 8px;
  
  .el-icon {
    font-size: 16px;
    color: var(--el-text-color-secondary);
  }
}

.template-preview {
  background: var(--el-fill-color-light);
  border-radius: 6px;
  padding: 16px;

  .preview-title {
    font-size: 14px;
    color: var(--el-text-color-secondary);
    margin-bottom: 8px;
  }

  .preview-content {
    font-size: 14px;
    color: var(--el-text-color-regular);
    line-height: 1.6;
  }
}
</style> 